import React, { useCallback, useEffect, useReducer, useRef } from 'react';
import PropTypes from 'prop-types';
import SublimitListComponent from './header/SublimitListComponent';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import {
  loadFacilitySublimits,
  saveFacilitySublimit,
  deleteFacilitySublimit,
  updateFacilitySublimitFilters,
  updateFacilitySublimitFinancingStructureFilters
} from '../actionCreators';
import { getFacilitySublimits, getFacilityAmount } from '../selectors';
import { reducer, initialState } from '../reducers/containerReducer';
import { shape, required, items } from '@totalsoft/pure-validations';
import { useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { useDirtyInfo } from '@totalsoft/change-tracking-react';

const validatorFacilitySublimits = items(
  shape({
    sublimitCode: required,
    sublimitName: required,
    sublimitValue: required
  })
);

const contentEqual = (prev, next) =>
  prev.loading === next.loading && prev.loaded === next.loaded && prev.content.length === next.content.length;

const FacilitySublimitsContainer = ({ documentId, siteId }) => {
  const dispatch = useDispatch();
  const [localState, localDispatch] = useReducer(reducer, initialState);
  const [dirtyInfo, setDirtyInfo] = useDirtyInfo();

  const [validationFacilitySublimits, validateFacilitySublimits] = useDirtyFieldValidation(validatorFacilitySublimits, dirtyInfo);

  const { loading, loaded, content: sublimits } = useSelector(state => getFacilitySublimits(documentId, state), contentEqual);

  const { facilityAmount } = useSelector(state => getFacilityAmount(documentId, state), shallowEqual);

  useEffect(() => {
    if (!documentId) return;
    dispatch(loadFacilitySublimits(documentId, siteId));
  }, [dispatch, documentId, siteId]);

  useEffect(() => {
    if (!documentId || !loaded) return;
    localDispatch({ type: 'OnInitState', sublimits });
  }, [documentId, loaded, sublimits]);

  useEffect(() => {
    validateFacilitySublimits(localState.sublimits, dirtyInfo);
  }, [localState.sublimits, dirtyInfo, validateFacilitySublimits]);

  const sublimitsRef = useRef(localState.sublimits);
  sublimitsRef.current = localState.sublimits;

  const onPropertyChanged = useCallback(
    (property, propertyValue, index) => {
      localDispatch({
        type: 'OnSublimitHeaderChanged',
        property,
        propertyValue: propertyValue,
        index: index
      });
      if (property === 'sublimitValue') {
        const sublimit = sublimitsRef.current[index];
        localDispatch({
          type: 'OnSublimitHeaderChanged',
          property: 'availableAmount',
          propertyValue: propertyValue - (sublimit?.blockedAmount ?? 0) - (sublimit?.usedAmount ?? 0),
          index: index
        });
      }
      setDirtyInfo(`${index}.${property}`);
    },
    [setDirtyInfo]
  );

  const onSave = useCallback(
    (type, data, callback = () => {}) => {
      switch (type) {
        case 'OnSaveSublimit': {
          const { sublimit, index } = data;
          if (!validateFacilitySublimits(sublimitsRef.current)) return;
          localDispatch({ type: 'BeforeSavingSublimit', index });
          return dispatch(saveFacilitySublimit(sublimit, documentId, siteId, callback));
        }
        case 'OnDeleteSublimit': {
          return dispatch(deleteFacilitySublimit(data, documentId, siteId, callback));
        }
        case 'FiltersUpdate': {
          if (data.context === 'SUBLIMITFILTERS') return dispatch(updateFacilitySublimitFilters(data, documentId, siteId, callback));
          else return dispatch(updateFacilitySublimitFinancingStructureFilters(data, documentId, siteId, callback));
        }
        default:
          return;
      }
    },
    [dispatch, documentId, siteId, validateFacilitySublimits]
  );

  return (
    <SublimitListComponent
      documentId={documentId}
      siteId={siteId}
      loading={loading}
      sublimits={localState.sublimits}
      defaultSublimitValue={facilityAmount}
      validation={validationFacilitySublimits}
      onChange={onPropertyChanged}
      onSave={onSave}
      dispatch={localDispatch}
    />
  );
};

FacilitySublimitsContainer.propTypes = {
  documentId: PropTypes.number.isRequired,
  siteId: PropTypes.number.isRequired
};

export default FacilitySublimitsContainer;
